import type { LocationQuery } from 'vue-router'
import useOdoscopeSearchAppliedFilters from './useOdoscopeSearchAppliedFilters'
import { useRoute } from '#app/composables/router'
import type { RangeTuple } from '~/components/ui/sliders/RangeSlider.vue'

export default () => {
  const route = useRoute()
  const router = useRouter()

  const trackingEvents = useTrackingEvents() as {
    trackFilterApply?: (key: string, value: string) => void
  }
  const trackFilterApply = trackingEvents.trackFilterApply || (() => undefined)

  const { appliedFilter, appliedFiltersCount } =
    useOdoscopeSearchAppliedFilters()

  const areFiltersUpdated = ref(false)
  const areFiltersCleared = ref(false)

  const createNewAttributeQuery = (
    slug: string,
    values?: string[],
  ): LocationQuery => {
    const attributeFilterQuery: LocationQuery = {}
    const query = { ...route.query }
    const attribute = appliedFilter.value.attributes?.find(
      (attribute) => attribute.key === slug,
    )

    if (!values || !values.length) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete query[slug]
      return query
    }

    const key = slug

    if (!attribute) {
      attributeFilterQuery[key] = `${values.join(',')}`
      return { ...query, ...attributeFilterQuery }
    }

    if (attribute?.type !== 'attributes') {
      return query
    }

    if (values.length === 0) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete query[key]
      return query
    }

    attributeFilterQuery[key] = `${values.join(',')}`

    return { ...query, ...attributeFilterQuery }
  }

  const createNewPriceQuery = (prices: RangeTuple): LocationQuery => {
    const query = { ...route.query }

    // Always set both min and max together so stale values don't persist
    query['minPrice'] = prices[0].toString()
    query['maxPrice'] = prices[1].toString()

    return query
  }

  const createNewBoolAttributeQuery = (
    slug: string,
    value: boolean,
  ): LocationQuery => {
    const booleanFilterQuery: Record<string, string> = {}
    const query = { ...route.query }

    const attribute = appliedFilter.value.attributes?.find(
      (attribute) => attribute.key === slug,
    )
    const key = slug

    if (!attribute) {
      if (!value) {
        return query
      }

      booleanFilterQuery[key] = String(value)
      return { ...query, ...booleanFilterQuery }
    }

    if (attribute.type !== 'boolean') {
      return query
    }

    if (!value) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete query[key]
      return query
    }

    booleanFilterQuery[key] = String(value)
    return { ...query, ...booleanFilterQuery }
  }

  const applyAttributeFilter = async (
    slug: string,
    value: string | string[],
  ) => {
    const values = Array.isArray(value) ? value : [value]
    const filters = createNewAttributeQuery(slug, values)
    trackFilterApply(slug, values.toString())
    await applyFilters(filters)
  }

  const applyBooleanFilter = async (slug: string, value: boolean) => {
    const filters = createNewBoolAttributeQuery(slug, value)
    trackFilterApply(slug, value.toString())
    await applyFilters(filters)
  }

  const applyPriceFilter = async (prices: RangeTuple) => {
    const filters = createNewPriceQuery(prices)
    trackFilterApply('prices', prices.join(','))
    await applyFilters(filters)
  }

  const resetFilter = async (key: string) => {
    const query = { ...route.query }
    const filterKey = key

    if (!query[filterKey]) {
      return
    }

    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete query[filterKey]
    await applyFilters(query)
  }

  const resetPriceFilter = async () => {
    const query = { ...route.query }

    if (!query['minPrice'] && !query['maxPrice']) {
      return
    }

    delete query['minPrice']
    delete query['maxPrice']

    await applyFilters(query)
  }

  const resetFilters = async () => {
    await applyFilters({})
    areFiltersCleared.value = true
    areFiltersUpdated.value = false

    setTimeout(() => {
      areFiltersCleared.value = false
    }, 3000)
  }

  const applyFilters = async (filter: LocationQuery, scrollToTop = true) => {
    const newQuery = { ...filter }

    if ('page' in newQuery) {
      delete newQuery.page
    }

    // Should not apply reset all filter if appliedFilter is empty
    if (!appliedFiltersCount.value && !Object.keys(filter).length) {
      return
    }

    await router.push({ query: { ...newQuery } })

    areFiltersUpdated.value = true

    if (scrollToTop) {
      window.scroll({ behavior: 'smooth', top: 0 })
    }
  }

  return {
    resetFilter,
    resetFilters,
    resetPriceFilter,
    applyFilters,
    applyAttributeFilter,
    applyPriceFilter,
    applyBooleanFilter,
    areFiltersCleared,
  }
}
